import type { ReactNode } from "react";
import { Collapsible } from "@/shared/ui/Collapsible";
import { useOrderShipments } from "../../api";
import type { Order, OrderShipment, ShipmentAddress } from "../../types";
import { addressKey, addressLine, addressWho, placeKey } from "./ShipmentsSection";

/** L'indirizzo di fatturazione ha gli stessi campi di uno di spedizione piu'
 *  il codice fiscale. */
type InvoiceAddress = ShipmentAddress & { cf: string | null };

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v.trim() : null;
}

/** Chi paga: la persona quando c'e', l'azienda altrimenti. ⚠️ L'OPPOSTO di
 *  `addressWho` (che preferisce l'azienda): qui l'intestatario e' la persona e
 *  l'azienda ha una riga sua, come nella spec visiva. */
function invoiceWho(a: InvoiceAddress): string | null {
  return [a.name, a.surname].filter(Boolean).join(" ") || a.company;
}

/** La FATTURAZIONE non e' un campo del modello (esistono solo gli `shipping_*`):
 *  vive in `raw.invoice_address`, JSON Poleepo che arriva gia' al browser e che
 *  nessuno schema valida a monte → narrowing qui, campo per campo. Serve
 *  davvero: 799 ordini su 4.124 (19%) hanno fatturazione != spedizione. */
function invoiceAddress(order: Order): InvoiceAddress | null {
  const raw = order.raw?.invoice_address;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const addr: InvoiceAddress = {
    id: null,
    cf: str(r.cf),
    company: str(r.company),
    name: str(r.name),
    surname: str(r.surname),
    phone: str(r.phone),
    address: str(r.address),
    postcode: str(r.postcode),
    city: str(r.city),
    state_iso: str(r.state_iso),
    country_iso: str(r.country_iso),
  };
  // Chiave presente ma tutta vuota → nessuna fatturazione, non una scheda di
  // trattini che finge un dato che Poleepo non ha mandato.
  return Object.values(addr).some((v) => v !== null) ? addr : null;
}

/** La fattura va allo STESSO posto del pacco? `null` quando la fatturazione non
 *  c'e': un confronto con un lato solo non e' un «no», e' un «non lo so».
 *
 *  Esportata perche' la risposta si legge in DUE punti — nel titolo di questa
 *  sezione e come pill nella fascia cliente, dove ormai vive l'indirizzo di
 *  spedizione. Scritta due volte darebbe due risposte alla stessa domanda.
 *
 *  Confronto su chi + via + luogo, normalizzati. I due lati arrivano da
 *  sorgenti diverse ma componibili nella stessa forma: `Order.customer.city`
 *  e' gia' «CAP Comune PROV» (la compone `mapOrder`), che e' esattamente la
 *  tripla postcode/city/state_iso della fatturazione. Fuori telefono, paese e
 *  C.F.: non dicono se il pacco e la fattura vanno nello stesso posto. */
export function fatturazioneCoincide(order: Order): boolean | null {
  const invoice = invoiceAddress(order);
  if (!invoice) return null;
  const c = order.customer;
  const shipKey = placeKey(c.name, c.address, [c.city]);
  const invoiceKey = placeKey(invoiceWho(invoice) ?? "", invoice.address, [
    invoice.postcode,
    invoice.city,
    invoice.state_iso,
  ]);
  if (!shipKey || !invoiceKey) return null;
  return shipKey === invoiceKey;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="whitespace-nowrap text-[12px] font-semibold text-ink">{label}</dt>
      <dd className="m-0 break-words text-ink">{children}</dd>
    </>
  );
}

const DL_CLASS = "m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px]";

/** Riga «Citta»: CAP Comune (PROV) · PAESE. Il paese solo se non e' IT. */
function cityLine(a: InvoiceAddress): string {
  const city = [a.postcode, a.city].filter(Boolean).join(" ");
  const place = city && a.state_iso ? `${city} (${a.state_iso})` : city;
  return [place, a.country_iso && a.country_iso !== "IT" ? a.country_iso : null]
    .filter(Boolean)
    .join(" · ");
}

function AddressLines({ addr, sub }: { addr: ShipmentAddress; sub?: string }) {
  // `addressWho` puo' essere null (Poleepo non dichiara il nome del nostro
  // magazzino sul sender): senza nome resta la sola via, che basta a se'.
  const who = addressWho(addr);
  return (
    <>
      {who && <span className="font-semibold">{who}</span>}
      <span className={who ? "block text-ink" : "font-semibold"}>{addressLine(addr)}</span>
      {sub && <span className="block text-[11.5px] text-ink">{sub}</span>}
    </>
  );
}

/** Corpo della fatturazione. I campi assenti si OMETTONO: una riga «non
 *  presente» occupa lo spazio di un dato senza esserlo, e in una colonna da
 *  320px lo spazio e' la risorsa scarsa. */
function InvoiceBody({ invoice }: { invoice: InvoiceAddress }) {
  const who = invoiceWho(invoice);
  const luogo = cityLine(invoice);
  return (
    <dl className={DL_CLASS}>
      {who && (
        <Field label="Intest.">
          <span className="font-semibold">{who}</span>
        </Field>
      )}
      {invoice.address && <Field label="Via">{invoice.address}</Field>}
      {luogo && <Field label="Città">{luogo}</Field>}
      {invoice.phone && (
        <Field label="Tel">
          <span className="font-mono">{invoice.phone}</span>
        </Field>
      )}
      {invoice.cf && (
        <Field label="C.F.">
          <span className="font-mono">{invoice.cf}</span>
        </Field>
      )}
      {/* L'azienda ha una riga sua solo se non e' gia' l'intestatario. */}
      {invoice.company && invoice.company !== who && (
        <Field label="Azienda">{invoice.company}</Field>
      )}
    </dl>
  );
}

/** Mittenti/Ritiro: c'e' solo sui 29 ordini con 2+ spedizioni, gli unici per
 *  cui il per-spedizione arriva al browser (`useOrderShipments`).
 *
 *  ⚠️ Un ritiro per spedizione, il mittente UNA volta quando e' lo stesso: su
 *  406646 il mittente e' BS COM su entrambe e a cambiare e' solo il ritiro.
 *  Chiamarli «due mittenti» sarebbe falso. */
function SendersBody({ shipments }: { shipments: OrderShipment[] }) {
  const senders = shipments
    .map((s) => s.sender_address)
    .filter((a): a is ShipmentAddress => a !== null);
  const oneSender = senders.length > 0 && new Set(senders.map((a) => addressKey(a))).size === 1;

  return (
    <dl className={DL_CLASS}>
      {shipments.map((s, i) => (
        <Field key={`ritiro-${s.id}`} label={`Ritiro ${i + 1}`}>
          {s.pickup_address ? (
            <AddressLines addr={s.pickup_address} />
          ) : (
            <span className="text-ink">n/d</span>
          )}
        </Field>
      ))}
      {oneSender && senders[0] ? (
        <Field label="Mittente">
          <AddressLines
            addr={senders[0]}
            sub={`su ${shipments.length === 2 ? "entrambe le" : "tutte le"} spedizioni`}
          />
        </Field>
      ) : (
        senders.map((a, i) => (
          <Field key={`mittente-${i}`} label={`Mittente ${i + 1}`}>
            <AddressLines addr={a} />
          </Field>
        ))
      )}
    </dl>
  );
}

/** Gli indirizzi che NON sono quello di spedizione.
 *
 *  La spedizione stava anche qui: era l'unica duplicazione strutturale della
 *  pagina — lo stesso destinatario in due riquadri, a 20 cm di distanza. Ora
 *  vive una volta sola, nella fascia cliente, e qui resta il confronto: il
 *  titolo dice gia' la risposta («= spedizione» / «DIVERSA»), cosi' i 3 ordini
 *  su 4 in cui coincidono non costano nemmeno un'apertura. */
export function AddressesSection({ order }: { order: Order }) {
  const shipments = useOrderShipments(order);
  const invoice = invoiceAddress(order);
  const coincide = fatturazioneCoincide(order);
  const pickupPoints = new Set(shipments.map((s) => addressKey(s.pickup_address)).filter(Boolean))
    .size;

  return (
    <div className="mb-4 space-y-3">
      <Collapsible
        id="fatturazione"
        title="Intestazione fattura"
        area="cliente"
        summary={
          !invoice
            ? "· nessun indirizzo nei dati Poleepo"
            : coincide === null
              ? undefined
              : coincide
                ? "· = spedizione"
                : "· DIVERSA"
        }
        // Diversa = c'e' qualcosa da guardare: si apre da sola, senza tono
        // ambra (non e' un lavoro da fare, e' un dato da leggere).
        defaultOpen={coincide === false}
      >
        {invoice ? (
          <InvoiceBody invoice={invoice} />
        ) : (
          <p className="m-0 text-[13px] text-ink">
            Nessun indirizzo di fatturazione nei dati Poleepo.
          </p>
        )}
      </Collapsible>

      {shipments.length > 0 && (
        <Collapsible
          id="mittenti-ritiro"
          title="Mittenti / Ritiro"
          area="cliente"
          // Mai «N mittenti»: quello che cambia e' DOVE si ritira.
          summary={
            pickupPoints > 1
              ? `· ${pickupPoints} punti di ritiro diversi`
              : `· ${shipments.length} spedizioni`
          }
        >
          <SendersBody shipments={shipments} />
        </Collapsible>
      )}
    </div>
  );
}
